"use client";

import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useRef, useState, useTransition } from "react";
import { createPortal } from "react-dom";
import type {
  WShopAdditionalSearchOptionsContent,
  WShopFilterPriceRange,
  WShopFilterProducerFacet,
  WShopFilterSubcategoryFacet,
  WShopFilterVariantFacet,
} from "@/lib/api/cms-window-content";
import { useT } from "@/lib/i18n/context";
import {
  wShopAdditionalSearchOptionsTranslations,
  type WShopAdditionalSearchOptionsTranslations,
} from "./WShopAdditionalSearchOptions.i18n";
import { Input, Checkbox, RangeSlider } from "@/components/ui/Form";
import { CSHOP_ACTIVE_FILTERS_SLOT_ID } from "@/components/catalogs/CShopCategory/CShopCategory";
import { toast } from "@/lib/notifications/toast";
import styles from "./WShopAdditionalSearchOptions.module.css";

/**
 * WShopAdditionalSearchOptions - panel filtrów sklepu (Etap 3b migracji).
 *
 * **Kontekst:** okno renderowane w sidebarze podstrony kategorii sklepu,
 * komplementarne do `CShopCategory` (który filtruje listę produktów według
 * URL params które to okno submituje).
 *
 * **URL params:**
 *   - `categorys[]=X` - subkategorie (checkbox list)
 *   - `producent[]=Y` - producenci (checkbox list)
 *   - `variants[N][]=Z` - warianty per feature (checkbox list)
 *   - `min-price` / `max-price` - zakres cen
 *   - `q` - keyword search
 *
 * **UX flow:**
 *   1. Sekcje filtrów (widoczne wg config admina)
 *   2. Chipy aktywnych filtrów pod formularzem (klik x usuwa pojedynczy filtr)
 *   3. Auto-apply z debounce - kazda zmiana kontrolki (input, checkbox, slider)
 *      startuje timer 300ms; ostatnia zmiana wygrywa (rapid clicks / drag =
 *      pojedynczy router.push zamiast N)
 *   4. "Wyczyść wszystko" -> nawigacja do bazowego URL katalogu
 *
 * **Controlled state (nie uncontrolled + remount):** filtry trzymamy w useState,
 * kliki updateuja state natychmiast, debounce pushuje URL po 300ms. Remount
 * formularza (defaultValue/defaultChecked + `key` bumping) BYL zrodlem race
 * condition - fresh content po pushu remontowal form w trakcie klikow usera,
 * kolejne kliki trafialy w DOM elementy ktore zostawaly odmontowane -> klik
 * LOST. Controlled state eliminuje ten wyscig: user's kliki nigdy nie znikaja,
 * state jest jedynym zrodlem prawdy.
 *
 * **Sync z URL:** stan resetuje sie do URL gdy URL zmieni sie EXTERNALLY (chip
 * x, "Wyczysc wszystko", back button, inicjalny render z filtrami). NASZ wlasny
 * `router.push` NIE triggeruje resetu (`lastPushedRef` przechowuje ostatnio
 * pushnietego searchParams - useEffect skipuje jesli sie zgadza).
 *
 * **Anty-pattern:** `scroll: false` na router.push - user zostaje przy panelu
 * filtrów, lista produktów obok się przeładuje.
 */
export function WShopAdditionalSearchOptions({
  content,
}: WShopAdditionalSearchOptionsProps) {
  const t = useT(wShopAdditionalSearchOptionsTranslations);
  const router = useRouter();
  const searchParams = useSearchParams();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* `useTransition` opakowuje wlasny `router.push` — `applyPending` pozwala
     wykryc dokladnie moment gdy Next.js dobil SSR nowej strony z filtrami
     (bez tego user, ktory zmienil checkbox, mogl nie zauwazyc odswiezenia
     listy produktow w sasiednim panelu). External URL change (back button,
     chip klikniety w innym miejscu) NIE leci przez ten transition, wiec toast
     odpala sie TYLKO po realnej aplikacji filtrow z tego panelu. */
  const [applyPending, startApplyTransition] = useTransition();

  const priceRange = content?.facets.price_range ?? null;
  const applyHref = content?.endpoints.apply_href ?? "";

  const [state, setState] = useState<FilterState>(() =>
    parseStateFromURL(searchParams, priceRange),
  );

  /* Portal target dla chipow aktywnych filtrow (renderowany przez `CShopCategory`
     obok toolbara z sortem). Chipy fizycznie zyja w tym drzewie (state +
     handlery = ta sama instancja), portal przenosi tylko warstwe wizualna do
     slotu w komponencie kategorii. Slot musi byc stabilny w DOM (poza galezia
     sortPending w CShopCategory), inaczej useEffect [] przegapilby remont. */
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setPortalTarget(document.getElementById(CSHOP_ACTIVE_FILTERS_SLOT_ID));
  }, []);

  /* Ref do state (dla stabilnego applyFilters - inaczej deps rekreowalyby
     callback co powodowaloby recreate scheduleApply, itd). */
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  /* Sync z URL gdy zmieni sie EXTERNALLY (chip x, back, clear all).
     `lastPushedRef` init = current searchParams (mount = brak "own push"),
     kazdy applyFilters aktualizuje ref przed router.push. Gdy useEffect
     wykryje ze cur === ref = to nasz push, skip. Inaczej = external, sync. */
  const lastPushedRef = useRef<string>(searchParams.toString());
  useEffect(() => {
    const cur = searchParams.toString();
    if (cur === lastPushedRef.current) return;
    lastPushedRef.current = cur;
    /* External URL change - anuluj pending debounce (user pewnie kliknal chip
       lub uzyl back button, jego intencja jest wyzsza od pending state z
       poprzednich klikow w panelu). */
    if (timerRef.current) clearTimeout(timerRef.current);
    setState(parseStateFromURL(searchParams, priceRange));
  }, [searchParams, priceRange]);

  /* Cleanup timera na unmount. */
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const applyFilters = useCallback(() => {
    const s = stateRef.current;
    const params = new URLSearchParams();

    if (s.query.trim() !== "") params.set("q", s.query.trim());

    for (const [key, values] of Object.entries(s.checkedByKey)) {
      for (const v of values) params.append(key, v);
    }

    /* Cena: pomijamy gdy zakres = pelny zakres kategorii. */
    if (priceRange && s.price) {
      const [lo, hi] = s.price;
      if (priceRange.min == null || lo > priceRange.min) {
        params.set("min-price", String(lo));
      }
      if (priceRange.max == null || hi < priceRange.max) {
        params.set("max-price", String(hi));
      }
    }

    /* Preservuj sort z aktualnego URL (nie chcemy resetowac sortowania). */
    const sort = searchParams.get("sort");
    const sortType = searchParams.get("sort_type");
    if (sort) params.set("sort", sort);
    if (sortType) params.set("sort_type", sortType);

    const qs = params.toString();
    /* Zapisz ref PRZED router.push - inaczej useEffect wystrzeli przed
       synchronicznym setem i potraktuje zmiane URL jako external. */
    lastPushedRef.current = qs;
    const base = extractPath(applyHref);
    const target = qs ? `${base}?${qs}` : base;
    /* `startTransition` — dostarcza `applyPending`, ktory nam mowi kiedy
       Next.js dobil SSR nowej strony z filtrami (do wystawienia toast'a). */
    startApplyTransition(() => {
      router.push(target, { scroll: false });
    });
  }, [applyHref, priceRange, router, searchParams]);

  /* Toast potwierdzajacy zaaplikowanie filtrow — bez tego user, ktory
     klikal checkboxy w panelu, moze nie zauwazyc odswiezenia listy produktow
     obok (skeleton mignal, kafelki wygladaja podobnie). Fire tylko na
     przejsciu applyPending true->false (nie na mount, gdzie pending=false). */
  const prevApplyPendingRef = useRef(applyPending);
  useEffect(() => {
    if (prevApplyPendingRef.current && !applyPending) {
      toast.success(t.resultsUpdated);
    }
    prevApplyPendingRef.current = applyPending;
  }, [applyPending, t.resultsUpdated]);

  const scheduleApply = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(applyFilters, 300);
  }, [applyFilters]);

  const onQueryChange = useCallback(
    (v: string) => {
      setState((s) => ({ ...s, query: v }));
      scheduleApply();
    },
    [scheduleApply],
  );

  const onToggleChecked = useCallback(
    (key: string, value: string, checked: boolean) => {
      setState((s) => {
        const set = new Set(s.checkedByKey[key] ?? []);
        if (checked) set.add(value);
        else set.delete(value);
        return {
          ...s,
          checkedByKey: { ...s.checkedByKey, [key]: Array.from(set) },
        };
      });
      scheduleApply();
    },
    [scheduleApply],
  );

  const onPriceChange = useCallback(
    (range: [number, number]) => {
      setState((s) => ({ ...s, price: range }));
      scheduleApply();
    },
    [scheduleApply],
  );

  const onClearAll = useCallback(() => {
    setState((s) => ({
      query: "",
      checkedByKey: {},
      price: priceRange
        ? [priceRange.min ?? 0, priceRange.max ?? 0]
        : s.price,
    }));
    scheduleApply();
  }, [priceRange, scheduleApply]);

  /* Chip removal handlers - operuja bezposrednio na state (nie na URL). */
  const onRemoveChecked = useCallback(
    (key: string, value: string) => {
      onToggleChecked(key, value, false);
    },
    [onToggleChecked],
  );

  const onRemoveQuery = useCallback(() => {
    onQueryChange("");
  }, [onQueryChange]);

  const onRemovePriceMin = useCallback(() => {
    if (!priceRange) return;
    setState((s) => ({
      ...s,
      price: s.price ? [priceRange.min ?? 0, s.price[1]] : s.price,
    }));
    scheduleApply();
  }, [priceRange, scheduleApply]);

  const onRemovePriceMax = useCallback(() => {
    if (!priceRange) return;
    setState((s) => ({
      ...s,
      price: s.price ? [s.price[0], priceRange.max ?? 0] : s.price,
    }));
    scheduleApply();
  }, [priceRange, scheduleApply]);

  if (!content) {
    return (
      <div className={styles.root}>
        <div className={styles.empty}>{t.emptyContext}</div>
      </div>
    );
  }

  const { config, facets } = content;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    /* Enter w polu text - aplikujemy natychmiast bez czekania na debounce. */
    e.preventDefault();
    if (timerRef.current) clearTimeout(timerRef.current);
    applyFilters();
  };

  /* Chipy derived ze STATE (nie z content.active) - zeby uniknac race'u
     gdzie server-side content.active jest snapshot'em URL sprzed pushy usera,
     a state odzwierciedla aktualne kliki. Labels lookupujemy w facets. */
  const chips = buildChipsFromState(state, facets, {
    onRemoveChecked,
    onRemoveQuery,
    onRemovePriceMin,
    onRemovePriceMax,
    currency: facets.price_range?.currency ?? "",
  });

  /* Chipy aktywnych filtrow portalowane do `#cshop-active-filters-slot`
     w `CShopCategory` (obok toolbara z sortem). Chipy zyja logicznie w tym
     komponencie (state + handlery), a wizualnie pojawiaja sie w kontenerze
     kategorii. Gdy target jeszcze nie zamontowany (SSR / pierwszy render
     przed useEffect) — chipy pomijamy, po hydratacji efekt ustawia target
     i portal sie pojawia. Gdy panel filtrow renderuje sie bez CShopCategory
     na stronie (unlikely), chipy sie nie pokazuja — sam formularz nadal
     dziala i URL sie aktualizuje. */
  const chipsPortal =
    chips.length > 0 && portalTarget
      ? createPortal(
          <ActiveFiltersChips chips={chips} onClearAll={onClearAll} t={t} />,
          portalTarget,
        )
      : null;

  return (
    <div className={styles.root}>
      <h3 className={styles.title}>{content.title || t.title}</h3>

      {chipsPortal}

      <form onSubmit={handleSubmit} className={styles.form}>
        {config.show_query && (
          <FacetSection title={t.sections.query}>
            {/* Bez `<Field label>` - h4 z FacetSection pelni role naglowka sekcji;
                floating label kolidowal z custom placeholder ("Wpisz fraze..."
                nadpisywal sentinel ` ` -> :placeholder-shown aktywny -> label
                stal na pozycji placeholdera zamiast floating u gory).
                A11y: aria-label na input dla screen readerow. */}
            <Input
              type="search"
              name="q"
              value={state.query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder={t.queryPlaceholder}
              aria-label={t.sections.query}
            />
          </FacetSection>
        )}

        {config.show_categorys && facets.subcategories.length > 0 && (
          <FacetSection title={t.sections.categorys}>
            <CheckboxList
              facets={facets.subcategories}
              filterKey="categorys[]"
              checkedValues={state.checkedByKey["categorys[]"] ?? []}
              onToggle={onToggleChecked}
            />
          </FacetSection>
        )}

        {config.show_producent && facets.producers.length > 0 && (
          <FacetSection title={t.sections.producent}>
            <CheckboxList
              facets={facets.producers}
              filterKey="producent[]"
              checkedValues={state.checkedByKey["producent[]"] ?? []}
              onToggle={onToggleChecked}
            />
          </FacetSection>
        )}

        {config.show_variants && facets.variants.length > 0 && (
          <FacetSection title={t.sections.variants}>
            <VariantsList
              variants={facets.variants}
              checkedByKey={state.checkedByKey}
              onToggle={onToggleChecked}
            />
          </FacetSection>
        )}

        {config.show_price && facets.price_range && state.price && (
          <FacetSection title={t.sections.price}>
            <PriceSlider
              range={facets.price_range}
              value={state.price}
              onChange={onPriceChange}
              ariaLabelMin={t.priceFromLabel}
              ariaLabelMax={t.priceToLabel}
            />
          </FacetSection>
        )}
      </form>
    </div>
  );
}

export type WShopAdditionalSearchOptionsProps = {
  content: WShopAdditionalSearchOptionsContent | null;
};

/* Filter state - jedno zrodlo prawdy dla wszystkich kontrolek panelu. */
type FilterState = {
  query: string;
  /** Mapa "categorys[]" | "producent[]" | "variants[N][]" -> array wartosci. */
  checkedByKey: Record<string, string[]>;
  /** Zakres ceny [lo, hi] albo null gdy sekcja price niedostepna. */
  price: [number, number] | null;
};

function parseStateFromURL(
  searchParams: URLSearchParams | ReturnType<typeof useSearchParams>,
  priceRange: WShopFilterPriceRange | null,
): FilterState {
  const query = searchParams.get("q") ?? "";

  const checkedByKey: Record<string, string[]> = {};
  for (const [key, value] of searchParams.entries()) {
    /* Interesuja nas TYLKO klucze z sufiksem `[]` - to konwencja
       filtrow mnogich (categorys[], producent[], variants[N][]). */
    if (!key.endsWith("[]")) continue;
    (checkedByKey[key] ??= []).push(value);
  }

  let price: [number, number] | null = null;
  if (priceRange) {
    const bMin = priceRange.min ?? 0;
    const bMax = priceRange.max ?? 0;
    const urlMin = searchParams.get("min-price");
    const urlMax = searchParams.get("max-price");
    const parseOr = (raw: string | null, fallback: number): number => {
      if (raw == null) return fallback;
      const n = parseFloat(raw);
      return Number.isFinite(n) ? n : fallback;
    };
    const lo = Math.max(bMin, Math.min(bMax, parseOr(urlMin, bMin)));
    const hi = Math.min(bMax, Math.max(lo, parseOr(urlMax, bMax)));
    price = [lo, hi];
  }

  return { query, checkedByKey, price };
}

/* Active filter chips - klikalne buttony (nie linki), akcja usuwa filtr
   bezposrednio w state parent'a. */

type ActiveChip = {
  key: string;
  label: string;
  onRemove: () => void;
};

function ActiveFiltersChips({
  chips,
  onClearAll,
  t,
}: {
  chips: ActiveChip[];
  onClearAll: () => void;
  t: WShopAdditionalSearchOptionsTranslations;
}) {
  return (
    <div className={styles.chipsWrap}>
      <div className={styles.chipsHeader}>
        <span className={styles.chipsLabel}>{t.activeHeader}:</span>
        <button
          type="button"
          className={styles.clearAllLink}
          onClick={onClearAll}
        >
          {t.clearAll}
        </button>
      </div>
      <ul className={styles.chips}>
        {chips.map((chip) => (
          <li key={chip.key}>
            <button
              type="button"
              className={styles.chip}
              onClick={chip.onRemove}
              aria-label={`${t.removeChip}: ${chip.label}`}
            >
              <span className={styles.chipLabel}>{chip.label}</span>
              <span className={styles.chipRemove} aria-hidden="true">
                x
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Builduje liste chipow ze state (nie z content.active - unikamy race'u).
 * Labels lookupujemy w facets po ID. Facet moze byc `undefined` (np. gdy
 * server zwrocil content bez tego ID) - wtedy fallback do goleg ID w chipe.
 */
function buildChipsFromState(
  state: FilterState,
  facets: WShopAdditionalSearchOptionsContent["facets"],
  handlers: {
    onRemoveChecked: (key: string, value: string) => void;
    onRemoveQuery: () => void;
    onRemovePriceMin: () => void;
    onRemovePriceMax: () => void;
    currency: string;
  },
): ActiveChip[] {
  const out: ActiveChip[] = [];

  if (state.query.trim() !== "") {
    out.push({
      key: "q",
      label: `"${state.query}"`,
      onRemove: handlers.onRemoveQuery,
    });
  }

  for (const id of state.checkedByKey["categorys[]"] ?? []) {
    const facet = facets.subcategories.find((s) => String(s.id) === id);
    out.push({
      key: `categorys-${id}`,
      label: facet?.name ?? id,
      onRemove: () => handlers.onRemoveChecked("categorys[]", id),
    });
  }

  for (const id of state.checkedByKey["producent[]"] ?? []) {
    const facet = facets.producers.find((p) => String(p.id) === id);
    out.push({
      key: `producent-${id}`,
      label: facet?.name ?? id,
      onRemove: () => handlers.onRemoveChecked("producent[]", id),
    });
  }

  for (const feature of facets.variants) {
    const filterKey = `variants[${feature.id_feature}][]`;
    for (const id of state.checkedByKey[filterKey] ?? []) {
      const val = feature.values.find((v) => String(v.id) === id);
      out.push({
        key: `variants-${feature.id_feature}-${id}`,
        label: val?.name ?? id,
        onRemove: () => handlers.onRemoveChecked(filterKey, id),
      });
    }
  }

  if (facets.price_range && state.price) {
    const pr = facets.price_range;
    const [lo, hi] = state.price;
    const suffix = handlers.currency !== "" ? ` ${handlers.currency}` : "";
    if (pr.min != null && lo > pr.min) {
      out.push({
        key: "min-price",
        label: `>= ${lo.toFixed(2)}${suffix}`,
        onRemove: handlers.onRemovePriceMin,
      });
    }
    if (pr.max != null && hi < pr.max) {
      out.push({
        key: "max-price",
        label: `<= ${hi.toFixed(2)}${suffix}`,
        onRemove: handlers.onRemovePriceMax,
      });
    }
  }

  return out;
}

/* Facet section wrapper */

function FacetSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className={styles.section}>
      <h4 className={styles.sectionTitle}>{title}</h4>
      {children}
    </section>
  );
}

/* Checkbox list (subcategories, producers) - controlled */

function CheckboxList({
  facets,
  filterKey,
  checkedValues,
  onToggle,
}: {
  facets: (WShopFilterSubcategoryFacet | WShopFilterProducerFacet)[];
  filterKey: string;
  checkedValues: string[];
  onToggle: (key: string, value: string, checked: boolean) => void;
}) {
  const checkedSet = new Set(checkedValues);
  return (
    <ul className={styles.facetList}>
      {facets.map((f) => {
        const value = String(f.id);
        return (
          <li key={f.id} className={styles.facetItem}>
            <Checkbox
              name={filterKey}
              value={value}
              checked={checkedSet.has(value)}
              onChange={(e) => onToggle(filterKey, value, e.target.checked)}
              label={
                <span className={styles.facetLabel}>
                  <span>{f.name}</span>
                  {"count" in f && typeof f.count === "number" && f.count > 0 && (
                    <span className={styles.facetCount}>({f.count})</span>
                  )}
                </span>
              }
            />
          </li>
        );
      })}
    </ul>
  );
}

/* Variants list (per-feature checkbox groups) - controlled */

function VariantsList({
  variants,
  checkedByKey,
  onToggle,
}: {
  variants: WShopFilterVariantFacet[];
  checkedByKey: Record<string, string[]>;
  onToggle: (key: string, value: string, checked: boolean) => void;
}) {
  return (
    <div className={styles.variantGroups}>
      {variants.map((feature) => {
        const filterKey = `variants[${feature.id_feature}][]`;
        const checkedSet = new Set(checkedByKey[filterKey] ?? []);
        return (
          <div key={feature.id_feature} className={styles.variantGroup}>
            <div className={styles.variantGroupName}>{feature.name}</div>
            <ul className={styles.facetList}>
              {feature.values.map((val) => {
                const value = String(val.id);
                return (
                  <li key={val.id} className={styles.facetItem}>
                    <Checkbox
                      name={filterKey}
                      value={value}
                      checked={checkedSet.has(value)}
                      onChange={(e) =>
                        onToggle(filterKey, value, e.target.checked)
                      }
                      label={val.name}
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

/* Price range slider - controlled (state w parent) */

function PriceSlider({
  range,
  value,
  onChange,
  ariaLabelMin,
  ariaLabelMax,
}: {
  range: WShopFilterPriceRange;
  value: [number, number];
  onChange: (range: [number, number]) => void;
  ariaLabelMin: string;
  ariaLabelMax: string;
}) {
  /* Backend zwraca `min`/`max` = null gdy brak produktow w kategorii - wtedy
     sekcja `show_price` i tak nie powinna renderowac. Defensywnie fallback
     do 0..0 zeby TS nie krzyczal - wywolujacy sprawdza `facets.price_range`
     przed render'em. */
  const bMin = range.min ?? 0;
  const bMax = range.max ?? 0;
  const currency = range.currency;
  const formatValue = (v: number): string =>
    currency !== "" ? `${v.toFixed(2)} ${currency}` : v.toFixed(2);

  return (
    <RangeSlider
      min={bMin}
      max={bMax}
      step={0.01}
      value={value}
      onChange={onChange}
      ariaLabelMin={ariaLabelMin}
      ariaLabelMax={ariaLabelMax}
      formatValue={formatValue}
    />
  );
}

/* Helpers */

/**
 * Wyciaga sam pathname (bez query) - do budowy nowego URL'a z filtrami.
 * Zwraca "/" jesli URL zewnetrzny.
 */
function extractPath(rawUrl: string): string {
  if (!rawUrl) return "/";
  if (rawUrl.startsWith("/")) return rawUrl.split("?")[0];
  try {
    return new URL(rawUrl).pathname;
  } catch {
    return "/";
  }
}
